import { useState, useEffect, useCallback, useRef } from "react";
import { getMyTeamInfo } from "../Store/apiStore";
import decodeToken from "../lib/decodeToken";

/**
 * useTeamPermissions — live permission sync for team members.
 *
 * Instead of reading stale permissions from the JWT (which only updates
 * on login), this hook fetches the latest permissions from the server
 * and keeps them in sync via periodic polling.
 *
 * @param {number} pollIntervalMs  How often to re-fetch (default 60 s)
 * @returns {{ isTeamMember, canEditAgents, canAccessDialer, permissions, loading }}
 */
const useTeamPermissions = (pollIntervalMs = 60000) => {
  const teamOwnerId = localStorage.getItem("teamOwnerId");
  const isTeamMember = !!teamOwnerId;

  // Fallback: read from JWT until first server fetch completes
  const token = localStorage.getItem("token") || "";
  const decoded = decodeToken(token);
  const jwtPerms = (() => {
    const p = decoded?.permissions;
    if (!p) return {};
    if (typeof p === "string") {
      try { return JSON.parse(p); } catch { return {}; }
    }
    return p;
  })();

  // Role travels in the JWT too (fallback until the first server fetch).
  const jwtRole = decoded?.role || (jwtPerms?.manageBilling ? "admin" : null);

  const [permissions, setPermissions] = useState(jwtPerms);
  const [role, setRole] = useState(jwtRole);
  const [loading, setLoading] = useState(isTeamMember);
  const mountedRef = useRef(true);

  // Log the member out and send them to a fresh login. Clearing teamOwnerId +
  // onboardComplete means the next login runs the normal new-user flow (they
  // create their own agent) instead of the team-member path.
  const forceFreshLogout = useCallback((reason) => {
    localStorage.removeItem("token");
    localStorage.removeItem("teamOwnerId");
    localStorage.removeItem("onboardComplete");
    localStorage.removeItem("selectedAgentAvatar");
    sessionStorage.clear();
    window.location.href = `/login?reason=${reason}`;
  }, []);

  const fetchPermissions = useCallback(async () => {
    if (!isTeamMember) return;
    try {
      const data = await getMyTeamInfo();
      if (!mountedRef.current) return;

      // The ACTIVE context is gone but other contexts are still live — the owner
      // whose account is open removed or deactivated this member. Send them back
      // to the picker instead of logging them out: the memberships they still
      // hold are fine, and dropping the whole session would throw away access
      // that was never revoked.
      if (data.contextLost) {
        window.location.href = "/select-access?switch=1";
        return;
      }

      // Deactivated from every team (reversible block by the owner).
      if (data.forceLogout || data.isDeactivated) {
        forceFreshLogout("deactivated");
        return;
      }

      // Removed from EVERY team → the shared team-member account is gone. Log
      // out now so the CURRENT session doesn't linger with stale/invalid data;
      // the next login starts the fresh new-user flow (own agent).
      if (!data.isTeamMember) {
        forceFreshLogout("removed");
        return;
      }

      if (data.permissions) {
        const perms = typeof data.permissions === "string"
          ? JSON.parse(data.permissions)
          : data.permissions;
        setPermissions(perms);
      }
      if (data.role) setRole(data.role);
    } catch (err) {
      // 404/401 = the account no longer exists (removed from all teams, EndUser
      // deleted) → force a fresh logout so re-login runs the new-user flow.
      const status = err?.response?.status;
      if (status === 404 || status === 401) {
        forceFreshLogout("removed");
        return;
      }
      console.error("[useTeamPermissions] fetch error:", err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [isTeamMember, forceFreshLogout]);

  useEffect(() => {
    mountedRef.current = true;
    fetchPermissions();

    if (!isTeamMember) {
      setLoading(false);
      return;
    }

    const interval = setInterval(fetchPermissions, pollIntervalMs);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchPermissions, isTeamMember, pollIntervalMs]);

  // Admins behave like the owner — every capability is unlocked and they may
  // manage billing / upgrade agents. Non-team-members (owners) are unrestricted.
  const isAdmin = isTeamMember && role === "admin";

  return {
    isTeamMember,
    role: isTeamMember ? (role || "support") : null,
    isAdmin,
    canEditAgents: !isTeamMember || isAdmin || !!permissions?.editAgents,
    canAccessDialer: !isTeamMember || isAdmin || !!permissions?.dialer,
    canViewCallLogs: !isTeamMember || isAdmin || permissions?.viewCallLogs !== false,
    canViewAppointments: !isTeamMember || isAdmin || permissions?.viewAppointments !== false,
    // Contacts: admins always have access; support is locked by default.
    canViewContacts: !isTeamMember || isAdmin || !!permissions?.viewContacts,
    // Only owners and admin team members can pay / upgrade agents.
    canManageBilling: !isTeamMember || isAdmin,
    permissions,
    loading,
    refetch: fetchPermissions,
  };
};

export default useTeamPermissions;
